/**
 * Mint Platform Navigation Configuration
 *
 * This file defines the complete navigation structure for the Mint platform.
 * When working with other LLMs or exporting code, include this configuration
 * to ensure navigation consistency across all pages.
 *
 * Usage:
 * - Import this config in your Layout component
 * - Reference MINT_NAVIGATION for the nav items
 * - Use MINT_BRAND for branding consistency
 */
import React from 'react'
import {
  LayoutDashboardIcon,
  UsersIcon,
  ShieldIcon,
  DollarSignIcon,
  BarChart3Icon,
  PieChartIcon,
  BuildingIcon,
  TrendingUpIcon,
  AlertTriangleIcon,
  SettingsIcon,
  MonitorIcon,
} from 'lucide-react'
export const MINT_BRAND = {
  name: 'Mint',
  version: 'v2.0',
  tagline: 'Mint Platform',
}
// Toggle this to true when ready to show Victory Park pages
const SHOW_VICTORY_PARK = false
export interface NavItem {
  name: string
  path?: string
  icon?: string
  description?: string
  children?: NavItem[]
  isSection?: boolean
}
export const MINT_NAVIGATION: NavItem[] = [
  {
    name: 'Treasury',
    icon: 'BuildingIcon',
    description: 'Supplier management portal',
    children: [
      {
        name: 'Treasury Home',
        path: '/anchor-portal',
        icon: 'BuildingIcon',
        description: 'Buyer / anchor view',
      },
      {
        name: 'Supplier Funding',
        path: '/supplier-funding',
        icon: 'BuildingIcon',
        description: 'Suppliers accepting early-payment offers',
      },
    ],
  },
  {
    name: 'Liquidity Desk',
    icon: 'TrendingUpIcon',
    description: 'Liquidity management and risk',
    children: [
      {
        name: 'City Desk',
        path: '/city-desk',
        icon: 'MonitorIcon',
        description: 'Institutional credit rail demo',
      },
      {
        name: 'Portfolio Intelligence',
        path: '/liquidity-desk',
        icon: 'BarChart3Icon',
        description: 'Institutional execution console',
      },
      {
        name: 'Allocation Engine',
        path: '/capitalization',
        icon: 'DollarSignIcon',
        description: 'Fund management and allocation',
      },
      {
        name: 'Risk Management',
        path: '/risk-management',
        icon: 'AlertTriangleIcon',
        description: 'Risk monitoring and controls',
      },
    ],
  },
  {
    name: 'System Tools',
    icon: 'SettingsIcon',
    description: 'System administration and monitoring',
    children: [
      {
        name: 'Dashboard',
        path: '/dashboard',
        icon: 'LayoutDashboardIcon',
        description: 'Overview and key metrics',
      },
      {
        name: 'Agents',
        path: '/agents',
        icon: 'UsersIcon',
        description: 'AI agents and automation',
      },
      {
        name: 'Audit',
        path: '/audit',
        icon: 'ShieldIcon',
        description: 'Verification and compliance',
      },
      {
        name: 'Analytics',
        path: '/analytics',
        icon: 'PieChartIcon',
        description: 'Advanced analytics and reporting',
      },
    ],
  },
]
// Icon mapping for easy reference
export const ICON_MAP = {
  LayoutDashboardIcon,
  UsersIcon,
  ShieldIcon,
  DollarSignIcon,
  BarChart3Icon,
  PieChartIcon,
  BuildingIcon,
  TrendingUpIcon,
  AlertTriangleIcon,
  SettingsIcon,
  MonitorIcon,
}
/**
 * Helper function to get icon component by name
 */
export function getIconComponent(iconName: string, size = 18) {
  const IconComponent = ICON_MAP[iconName as keyof typeof ICON_MAP]
  if (IconComponent) {
    return React.createElement(IconComponent, { size })
  }
  return null
}
/**
 * EXPORT TEMPLATE FOR OTHER LLMs:
 *
 * Copy the following when prompting other LLMs to maintain Mint navigation:
 *
 * ---
 * The Mint platform has the following navigation structure:
 *
 * 1. Treasury - Supplier management portal
 * 2. Liquidity Desk (parent)
 *    - City Desk - Institutional credit rail demo
 *    - Portfolio Intelligence - Institutional execution console
 *    - Allocation Engine - Fund management and allocation
 *    - Risk Management - Risk monitoring and controls
 * 3. System Tools (section)
 *    - Dashboard - Overview and key metrics
 *    - Agents - AI agents and automation
 *    - Audit - Verification and compliance
 *    - Analytics - Advanced analytics and reporting
 *
 * All pages should include a sidebar with these navigation items.
 * The sidebar should have a dark gray background (bg-gray-800) with white text.
 * Brand name: "Mint" | Version: "v2.0"
 * ---
 */
